// Custom hook for managing Terms and Conditions data

import { useState, useEffect, useCallback } from 'react';
import { TermsService, TermsData, TermsApiResponse } from '../services/termsService';

interface UseTermsDataReturn {
  termsData: TermsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isValidData: boolean;
}

/**
 * Custom hook for fetching and managing terms and conditions data
 * @returns UseTermsDataReturn object with data, loading state, error, and refetch function
 */
export const useTermsData = (): UseTermsDataReturn => {
  const [termsData, setTermsData] = useState<TermsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTermsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
        const response: TermsApiResponse = await TermsService.fetchTermsData();
      
      if (response.success  && response.data) {
        setTermsData(response.data);
      } else {
        setError('Invalid response format from server');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load terms and conditions');
      setTermsData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    await fetchTermsData();
  }, [fetchTermsData]);

  // Check if the current terms data is valid
  const isValidData = TermsService.isValidTermsData(termsData);

  // Fetch data on component mount
  useEffect(() => {
    fetchTermsData();
  }, [fetchTermsData]);

  return {
    termsData,
    loading,
    error,
    refetch,
    isValidData,
  };
};

/**
 * Hook for getting formatted effective date
 * @param termsData - The terms data object
 * @returns Formatted date string or null
 */
export const useFormattedEffectiveDate = (termsData: TermsData | null): string | null => {
  if (!termsData?.document.effective_date) return null;
  
  try {
    return TermsService.formatEffectiveDate(termsData.document.effective_date);
  } catch {
    return termsData.document.effective_date;
  }
};